import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Shield, Lock, Mail, ArrowLeft, Send } from 'lucide-react';
import { adminLogin } from '../../api/Adminloginapi';
import { sendForgotPasswordOtp, verifyForgotPasswordOtp, resetPassword } from '../../api/forgotPasswordApi';
import { saveUser } from '../../utils/auth';

import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [resetStep, setResetStep] = useState(1);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const storedEmail = localStorage.getItem('userEmail');
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setInterval(() => {
        setResendCountdown(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendCountdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setError('');
      setIsLoading(true);

      const data = await adminLogin(email, password);


      // Debug
      //

      // Strict validation: must have success=true AND a valid token
      if (!data.success || !data.token) {
        throw new Error(data.message || 'Invalid email or password');
      }

      // Save token, email, and user
      sessionStorage.setItem("token", data.token);
      sessionStorage.setItem("userEmail", data.user?.email || email);
      saveUser(data.user);

      // Redirect after login
      navigate('/dashboard');

    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      setForgotMessage('Please enter your email address');
      return;
    }
    setForgotLoading(true);
    setForgotMessage('');
    try {
      const data = await sendForgotPasswordOtp(forgotEmail);
      if (data.success) {
        setForgotMessage('OTP has been sent to your email!');
        setResetStep(2);
        setResendCountdown(30);
      } else {
        setForgotMessage(data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setForgotMessage(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) {
      setForgotMessage('Please enter the OTP');
      return;
    }
    setForgotLoading(true);
    setForgotMessage('');
    try {
      const data = await verifyForgotPasswordOtp(forgotEmail, otp);
      if (data.success) {
        setForgotMessage('OTP verified successfully!');
        setResetToken(data.token || data.resetToken || '');
        setResetStep(3);
      } else {
        setForgotMessage(data.message || 'Invalid OTP');
      }
    } catch (err) {
      setForgotMessage(err.message || 'OTP verification failed. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmNewPassword) {
      setForgotMessage('Please fill in both password fields');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setForgotMessage('Passwords do not match!');
      return;
    }
    if (!resetToken) {
      setForgotMessage('Reset token missing. Please verify OTP again.');
      return;
    }
    setForgotLoading(true);
    setForgotMessage('');
    try {
      const data = await resetPassword(newPassword, resetToken);
      if (data.success) {
        setForgotMessage('Password updated successfully!');
        setTimeout(() => {
          handleCloseForgot();
        }, 1500);
      } else {
        setForgotMessage(data.message || 'Failed to reset password');
      }
    } catch (err) {
      setForgotMessage(err.message || 'Password reset failed. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleCloseForgot = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotMessage('');
    setForgotLoading(false);
    setResetStep(1);
    setOtp('');
    setNewPassword('');
    setConfirmNewPassword('');
    setResetToken('');
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
          <div className="shape shape-5"></div>
          <div className="shape shape-6"></div>
          <div className="particle particle-1"></div>
          <div className="particle particle-2"></div>
          <div className="particle particle-3"></div>
          <div className="particle particle-4"></div>
        </div>
      </div>

      <div className="login-card">
        <div className="login-logo">
          <div className="logo-wrapper">
            <img src="/assets/SST_logo.svg" alt="SST Logo" className="logo-image" />
            <div className="logo-glow"></div>
          </div>
          <h1 className="login-title">SST Admin</h1>
          <p className="login-subtitle">Sri Shiva Travels Management Portal</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email Address</label>
            <div className="input-wrapper">
              <Mail className="input-icon" size={18} />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                required
                autoComplete="email"
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">Password</label>
            <div className="input-wrapper">
              <Lock className="input-icon" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
                className="form-input form-input-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && <div className="login-error">{error}</div>}

          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="spinner" size={20} />
                Signing In...
              </>
            ) : (
              <>
                <Shield size={20} />
                Sign In
              </>
            )}
          </button>

          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#07A95B',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                padding: '4px 0',
                textDecoration: 'underline'
              }}
            >
              Forgot Password?
            </button>
          </div>
        </form>

        <div className="login-footer">
          <p>© 2024 Sri Shiva Travels. All rights reserved.</p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px',
            backdropFilter: 'blur(4px)'
          }}
          onClick={handleCloseForgot}
        >
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
              width: '100%',
              maxWidth: '400px',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                backgroundColor: '#07A95B',
                padding: '24px',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Mail size={22} />
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Forgot Password</h3>
              </div>
              <button
                onClick={handleCloseForgot}
                style={{ background: 'none', border: 'none', color: '#FFFFFF', cursor: 'pointer', padding: '4px' }}
              >
                <ArrowLeft size={20} />
              </button>
            </div>

            <div style={{ padding: '28px' }}>
              {resetStep === 1 && (
                <>
                  <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#4B5563' }}>
                    Enter your registered email address and we'll send you an OTP.
                  </p>

                  <div style={{ position: 'relative', marginBottom: '16px' }}>
                    <Mail
                      size={18}
                      style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }}
                    />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="Enter your email"
                      style={{
                        width: '100%',
                        padding: '12px 16px 12px 44px',
                        borderRadius: '10px',
                        border: '1.5px solid #E5E7EB',
                        fontSize: '14px',
                        outline: 'none',
                        backgroundColor: '#F9FAFB',
                        color: '#111827',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#07A95B'}
                      onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                    />
                  </div>

                  {forgotMessage && (
                    <div
                      style={{
                        fontSize: '13px',
                        color: forgotMessage.includes('sent') || forgotMessage.includes('success') ? '#05914D' : '#DC2626',
                        marginBottom: '16px',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        backgroundColor: forgotMessage.includes('sent') || forgotMessage.includes('success') ? '#ECFDF5' : '#FEF2F2'
                      }}
                    >
                      {forgotMessage}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={forgotLoading}
                    style={{
                      width: '100%',
                      backgroundColor: '#07A95B',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: forgotLoading ? 'not-allowed' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: forgotLoading ? 0.7 : 1
                    }}
                  >
                    {forgotLoading ? (
                      <>
                        <Loader2 size={18} className="spinner" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Send OTP
                      </>
                    )}
                  </button>
                </>
              )}

              {resetStep === 2 && (
                <>
                  <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#4B5563' }}>
                    Enter the OTP sent to <strong>{forgotEmail}</strong>
                  </p>

                  <div style={{ position: 'relative', marginBottom: '16px' }}>
                    <Lock
                      size={18}
                      style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }}
                    />
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="Enter OTP"
                      maxLength={6}
                      style={{
                        width: '100%',
                        padding: '12px 16px 12px 44px',
                        borderRadius: '10px',
                        border: '1.5px solid #E5E7EB',
                        fontSize: '14px',
                        outline: 'none',
                        backgroundColor: '#F9FAFB',
                        color: '#111827',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#07A95B'}
                      onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                    />
                  </div>

                  {forgotMessage && (
                    <div
                      style={{
                        fontSize: '13px',
                        color: forgotMessage.includes('success') ? '#05914D' : '#DC2626',
                        marginBottom: '16px',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        backgroundColor: forgotMessage.includes('success') ? '#ECFDF5' : '#FEF2F2'
                      }}
                    >
                      {forgotMessage}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={forgotLoading}
                    style={{
                      width: '100%',
                      backgroundColor: '#07A95B',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: forgotLoading ? 'not-allowed' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: forgotLoading ? 0.7 : 1
                    }}
                  >
                    {forgotLoading ? (
                      <>
                        <Loader2 size={18} className="spinner" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <Shield size={18} />
                        Verify OTP
                      </>
                    )}
                  </button>

                  <div style={{ textAlign: 'center', marginTop: '12px' }}>
                    {resendCountdown > 0 ? (
                      <span style={{ fontSize: '13px', color: '#9CA3AF', fontWeight: 500 }}>
                        Resend OTP in {resendCountdown}s
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={async () => {
                          setForgotMessage('');
                          setOtp('');
                          setResendCountdown(30);
                          try {
                            const data = await sendForgotPasswordOtp(forgotEmail);
                            if (data.success) {
                              setForgotMessage('OTP has been sent to your email!');
                            } else {
                              setForgotMessage(data.message || 'Failed to resend OTP');
                            }
                          } catch (err) {
                            setForgotMessage(err.message || 'Failed to resend OTP. Please try again.');
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#07A95B',
                          fontSize: '13px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                      >
                        Didn't receive OTP? Resend
                      </button>
                    )}
                  </div>
                </>
              )}

              {resetStep === 3 && (
                <>
                  <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#4B5563' }}>
                    Set your new password
                  </p>

                  <div style={{ position: 'relative', marginBottom: '16px' }}>
                    <Lock
                      size={18}
                      style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }}
                    />
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New Password"
                      style={{
                        width: '100%',
                        padding: '12px 16px 12px 44px',
                        borderRadius: '10px',
                        border: '1.5px solid #E5E7EB',
                        fontSize: '14px',
                        outline: 'none',
                        backgroundColor: '#F9FAFB',
                        color: '#111827',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#07A95B'}
                      onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                    />
                  </div>

                  <div style={{ position: 'relative', marginBottom: '16px' }}>
                    <Lock
                      size={18}
                      style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }}
                    />
                    <input
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      placeholder="Confirm Password"
                      style={{
                        width: '100%',
                        padding: '12px 16px 12px 44px',
                        borderRadius: '10px',
                        border: '1.5px solid #E5E7EB',
                        fontSize: '14px',
                        outline: 'none',
                        backgroundColor: '#F9FAFB',
                        color: '#111827',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#07A95B'}
                      onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                    />
                  </div>

                  {forgotMessage && (
                    <div
                      style={{
                        fontSize: '13px',
                        color: forgotMessage.includes('success') ? '#05914D' : '#DC2626',
                        marginBottom: '16px',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        backgroundColor: forgotMessage.includes('success') ? '#ECFDF5' : '#FEF2F2'
                      }}
                    >
                      {forgotMessage}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={forgotLoading}
                    style={{
                      width: '100%',
                      backgroundColor: '#07A95B',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: forgotLoading ? 'not-allowed' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: forgotLoading ? 0.7 : 1
                    }}
                  >
                    {forgotLoading ? (
                      <>
                        <Loader2 size={18} className="spinner" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <Lock size={18} />
                        Update Password
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
